import React, { useCallback, useEffect, useMemo } from 'react';
import { getFormInitialValues, initialize } from 'redux-form';
import { connect } from 'react-redux';
import { isEmpty, set } from 'lodash';

import FORM_NAMES from 'constants/formNames';
// @ts-ignore
import TrashTooltip from 'components/TrashTooltip';
// @ts-ignore
import { FormGroupTags } from 'components/Form';
import FieldDropdownItem from './FieldDropdownItem';
import SFormGroupTags, {
  SSeparator,
  SRefIntentContainer,
  SRefIntentWrapper,
  SText,
} from './styled';
import { RefIntentProps, Intent } from './types';

const PAGE = 1;
const SIZE = 50;

const getArchivedIntents = (intents: Intent[]) =>
  intents.length > 0 &&
  intents.filter(intent => intent.archived).map(intent => intent.name);

const RefIntent = ({
  allIntents,
  change,
  onDelete,
  name,
  intent,
  selectItem,
  removeItem,
  selectedIntents,
  additionalIntents,
  getAllIntents,
  isLoadingAllIntents,
  initialValues,
}: RefIntentProps) => {
  const archivedIntents = useMemo(() => getArchivedIntents(allIntents), [
    allIntents,
  ]);

  const getIntents = useCallback(
    (searchTerm = '') => {
      getAllIntents({
        page: PAGE,
        size: SIZE,
        name__icontains: searchTerm,
      });
    },
    [getAllIntents],
  );

  const initializeAllReferences = useCallback(() => {
    // api returns only intent_reference and no additional intents
    if (
      !isEmpty(intent) &&
      isEmpty(additionalIntents) &&
      isEmpty(selectedIntents)
    ) {
      const updatedValues = set(initialValues, `${name}.all_references`, [
        ...selectedIntents,
        intent?.name ?? '',
      ]);
      initialize(FORM_NAMES.INTENTS.INSPECT, { updatedValues });
    }

    // api returns intent_reference and additional intents
    if (
      !isEmpty(intent) &&
      !isEmpty(additionalIntents) &&
      isEmpty(selectedIntents)
    ) {
      const updatedValues = set(initialValues, `${name}.all_references`, [
        ...selectedIntents,
        intent?.name ?? '',
        ...additionalIntents.map(intent => intent.name),
      ]);
      initialize(FORM_NAMES.INTENTS.INSPECT, { updatedValues });
    }
  }, [intent, selectedIntents, additionalIntents, initialValues, name]);

  useEffect(() => {
    getIntents();
  }, [getIntents]);

  useEffect(() => {
    initializeAllReferences();
  }, [initializeAllReferences]);

  const getIntentsValidationRegex = (intents: Array<Intent>) => {
    if (!intents.length) {
      return new RegExp(/^(?!.*)*$/);
    }

    const regex = intents.map(intent => intent.name).join('|');

    return new RegExp(regex);
  };

  const getSuggestions = (
    inputValue: string,
    intents: Intent[],
    selected: string[],
  ) =>
    intents.filter(
      intent =>
        intent.name.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1 &&
        !selected.includes(intent.name),
    );

  const handleGetSuggestions = useCallback(
    inputValue => getSuggestions(inputValue, allIntents, selectedIntents),
    [allIntents, selectedIntents],
  );

  const handleAddTag = useCallback(
    tag => {
      if (
        tag &&
        !tag.archived &&
        allIntents.find(intent => intent.name === tag?.name)
      ) {
        selectItem(tag);
        getIntents();
      }
    },
    [selectItem, getIntents, allIntents],
  );

  const handleRemoveTag = useCallback(
    values => {
      removeItem(values);
    },
    [removeItem],
  );

  const handleChange = useCallback(
    values => {
      if (selectedIntents.length > values.length) {
        handleRemoveTag(values);
      }

      if (selectedIntents.length < values.length) {
        const tagName = values.find(
          (v: string) => !selectedIntents.includes(v),
        );

        if (tagName) {
          handleAddTag(allIntents.find(intent => intent.name === tagName));
        }
      }

      const foundNotArchived = values.filter(
        (val: string) => !archivedIntents.includes(val),
      );

      change(`${name}.all_references`, foundNotArchived);
    },
    [
      name,
      selectedIntents,
      archivedIntents,
      handleRemoveTag,
      change,
      handleAddTag,
      allIntents,
    ],
  );

  return (
    <SRefIntentContainer>
      <SRefIntentWrapper>
        <SText data-test="entity-label" level={4}>
          Intent Reference
        </SText>
        <SSeparator>-</SSeparator>
        <SFormGroupTags
          autoOpen
          component={FormGroupTags}
          getSuggestions={handleGetSuggestions}
          inputProps={{
            isLoading: isLoadingAllIntents,
            onChange: ({ target }: { target: HTMLInputElement }) =>
              getIntents(target.value),
          }}
          name={`${name}.all_references`}
          placeholder="Select or search an intent"
          suggestionItemComponent={FieldDropdownItem}
          type="text"
          validationRegex={getIntentsValidationRegex(allIntents)}
          onAddTag={handleAddTag}
          onChangeValues={handleChange}
        />
      </SRefIntentWrapper>
      <TrashTooltip text="Delete reference intent" onDelete={onDelete} />
    </SRefIntentContainer>
  );
};

const mapStateToProps = (state: any) => {
  const initialValues = getFormInitialValues(FORM_NAMES.INTENTS.INSPECT)(state);

  return { initialValues };
};

export default connect(mapStateToProps)(RefIntent);
